import { useMemo } from "react";
import { useEditableState } from "./hooks/useEditableState";
import { useEditableEffects } from "./hooks/useEditableEffects";
import { createValueConverter } from "./utils/valueConverters";
import { createEditHandlers } from "./utils/editHandlers";
import { ReadOnlyView } from "./components/ReadOnlyView";
import { EditView } from "./components/EditView";

const EditableSelectCell = ({
	value,
	row,
	fieldName = "status",
	onSave,
	getRecordId = (originalRow) => originalRow?.id,
	displayFormatter,
	options = [],
	isMulti = false,
	isClearable = true,
	isSearchable = true,
	formatOutput,
	disabled = false,
	className = "",
	readButtonTitle = "Editar selección",
	savingText = "Guardando…",
	cancelText = "Cancelar",
	saveText = "Guardar",
	expandOnEdit = true,
	placeholder = "Seleccionar...",
	noOptionsMessage = () => "Sin opciones",
	loadingMessage = () => "Cargando...",
	required = false,
	getOptionValue = (option) => option.value,
	getOptionLabel = (option) => option.label,
	isCreatable = false,
	onCreateOption,
	formatCreateLabel = (inputValue) => `Crear "${inputValue}"`,
	components = {},
	newOptionDefaults = {},
	filterOption,
}) => {
	const recordId = useMemo(() => getRecordId?.(row?.original), [getRecordId, row]);
	const cellKey = `${recordId}-${fieldName}`;

	const state = useEditableState(cellKey, options);
	const {
		isEditing,
		setIsEditing,
		setIsEditingState,
		selectedValue,
		setSelectedValue,
		isSaving,
		setIsSaving,
		errorText,
		setErrorText,
		dynamicOptions,
		setDynamicOptions,
		menuIsOpen,
		setMenuIsOpen,
		selectRef,
		isMountedRef,
		abortRef,
		containerRef,
		clearPersistedState,
	} = state;

	const valueToSelectValue = useMemo(() => createValueConverter(dynamicOptions, isMulti, getOptionValue), [dynamicOptions, isMulti, getOptionValue]);

	useEditableEffects({
		isEditing,
		menuIsOpen,
		value,
		options,
		containerRef,
		selectRef,
		isMountedRef,
		abortRef,
		setIsEditing,
		setSelectedValue,
		setErrorText,
		setDynamicOptions,
		valueToSelectValue,
	});

	const handlers = createEditHandlers({
		disabled,
		isSaving,
		required,
		recordId,
		row,
		fieldName,
		isMulti,
		formatOutput,
		getOptionValue,
		valueToSelectValue,
		value,
		onSave,
		selectedValue,
		setErrorText,
		setIsEditing,
		setIsEditingState,
		setSelectedValue,
		isMountedRef,
		setIsSaving,
		abortRef,
		clearPersistedState,
		newOptionDefaults,
		onCreateOption,
		setDynamicOptions,
	});

	const { beginEdit, cancelEdit, persistChange, handleKeyDown, handleCreateOption } = handlers;

	if (!isEditing) {
		return (
			<ReadOnlyView
				value={value}
				displayFormatter={displayFormatter}
				valueToSelectValue={valueToSelectValue}
				isMulti={isMulti}
				getOptionValue={getOptionValue}
				getOptionLabel={getOptionLabel}
				onEdit={beginEdit}
				readButtonTitle={readButtonTitle}
				disabled={disabled}
				className={className}
			/>
		);
	}

	return (
		<EditView
			containerRef={containerRef}
			expandOnEdit={expandOnEdit}
			isCreatable={isCreatable}
			selectRef={selectRef}
			selectedValue={selectedValue}
			setSelectedValue={setSelectedValue}
			handleCreateOption={handleCreateOption}
			dynamicOptions={dynamicOptions}
			isMulti={isMulti}
			isClearable={isClearable}
			isSearchable={isSearchable}
			isSaving={isSaving}
			disabled={disabled}
			placeholder={placeholder}
			noOptionsMessage={noOptionsMessage}
			loadingMessage={loadingMessage}
			formatCreateLabel={formatCreateLabel}
			getOptionValue={getOptionValue}
			getOptionLabel={getOptionLabel}
			handleKeyDown={handleKeyDown}
			setMenuIsOpen={setMenuIsOpen}
			components={components}
			persistChange={persistChange}
			cancelEdit={cancelEdit}
			savingText={savingText}
			saveText={saveText}
			cancelText={cancelText}
			errorText={errorText}
			className={className}
			filterOption={filterOption}
		/>
	);
};

export default EditableSelectCell;
